// src/services/BackupService.js
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  serverTimestamp,
  onSnapshot,
  Timestamp
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject,
  listAll,
  getMetadata
} from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { BACKUP_CONFIG } from './backupConfig';
import { 
  generateBackupId, 
  generateBackupName,
  createTimestamp,
  calculateBackupSize,
  formatFileSize,
  validateBackupIntegrity
} from './backupUtils';

class BackupService {
  constructor() {
    this.isBackupInProgress = false;
    this.currentBackupId = null;
    this.backupStats = {
      lastBackup: null,
      totalBackups: 0,
      lastBackupSize: 0,
      lastBackupDuration: 0
    };
    
    // Callbacks pour le monitoring
    this.progressCallbacks = new Set();
    this.statusCallbacks = new Set();
    
    console.log('🔧 BackupService initialisé');
  }

  /**
   * ====================================
   * MÉTHODES PRINCIPALES DE SAUVEGARDE
   * ====================================
   */

  /**
   * Créer une sauvegarde complète
   */
  async createFullBackup(options = {}) {
    const {
      userId = 'system',
      type = 'manual',
      description = '',
      includeCritical = true,
      includeSystem = true,
      includeOptional = false,
      collections: customCollections = null,
      automated = false
    } = options;

    if (this.isBackupInProgress) {
      throw new Error('Une sauvegarde est déjà en cours');
    }

    const startTime = Date.now();
    const backupId = generateBackupId(type);
    this.isBackupInProgress = true;
    this.currentBackupId = backupId;

    console.log(`🚀 Démarrage sauvegarde complète: ${backupId}`);
    
    try {
      // Notifier le début
      this.notifyProgress(backupId, 'started', 0, 'Initialisation de la sauvegarde...');

      // Déterminer les collections à sauvegarder
      const collectionsToBackup = this.getCollectionsToBackup({
        includeCritical,
        includeSystem,
        includeOptional,
        customCollections
      });

      console.log(`📂 Collections à sauvegarder: ${collectionsToBackup.join(', ')}`);

      // Créer l'entrée de sauvegarde
      const backupMetadata = {
        id: backupId,
        name: generateBackupName(type, new Date()),
        type,
        status: 'in_progress',
        createdBy: userId,
        createdAt: createTimestamp(),
        startedAt: createTimestamp(),
        description,
        automated,
        collectionsPlanned: collectionsToBackup,
        collectionsCompleted: [],
        metadata: {
          version: BACKUP_CONFIG.VERSION,
          totalCollections: collectionsToBackup.length,
          totalDocuments: 0,
          totalSize: 0,
          duration: 0,
          errors: [],
          warnings: []
        }
      };

      // Sauvegarder les métadonnées initiales
      await setDoc(doc(db, 'backups', backupId), backupMetadata);
      
      this.notifyProgress(backupId, 'progress', 5, 'Sauvegarde des collections...');

      // Sauvegarder chaque collection
      const backupData = {
        id: backupId,
        metadata: backupMetadata,
        collections: {},
        timestamp: Date.now(),
        version: BACKUP_CONFIG.VERSION
      };

      let totalDocuments = 0;
      let completedCollections = 0;

      for (const collectionName of collectionsToBackup) {
        try {
          console.log(`📊 Sauvegarde collection: ${collectionName}`);
          
          const progress = 10 + (completedCollections / collectionsToBackup.length) * 70;
          this.notifyProgress(
            backupId, 
            'progress', 
            Math.round(progress), 
            `Sauvegarde de la collection: ${collectionName}`
          );

          const collectionData = await this.backupCollection(collectionName);
          backupData.collections[collectionName] = collectionData;
          
          totalDocuments += collectionData.count;
          completedCollections++;

          // Mettre à jour les métadonnées
          await updateDoc(doc(db, 'backups', backupId), {
            collectionsCompleted: [...backupMetadata.collectionsCompleted, collectionName],
            'metadata.totalDocuments': totalDocuments
          });

        } catch (error) {
          console.error(`❌ Erreur sauvegarde collection ${collectionName}:`, error);
          backupData.collections[collectionName] = {
            error: error.message,
            timestamp: Date.now(),
            count: 0,
            documents: []
          };
          backupMetadata.metadata.errors.push(`${collectionName}: ${error.message}`);
        }
      }

      this.notifyProgress(backupId, 'progress', 80, 'Finalisation de la sauvegarde...');

      // Calculer la taille et finaliser
      const backupSize = calculateBackupSize(backupData);
      const duration = Date.now() - startTime;

      // Upload vers Firebase Storage
      const storageRef = ref(storage, `backups/${backupId}.json`);
      const backupBlob = new Blob([JSON.stringify(backupData, null, 2)], { 
        type: 'application/json' 
      });
      
      await uploadBytes(storageRef, backupBlob);
      const downloadURL = await getDownloadURL(storageRef);

      this.notifyProgress(backupId, 'progress', 95, 'Sauvegarde des métadonnées finales...');

      // Mettre à jour les métadonnées finales
      const finalMetadata = {
        status: 'completed',
        completedAt: createTimestamp(),
        downloadURL,
        storageRef: storageRef.fullPath,
        metadata: {
          ...backupMetadata.metadata,
          totalDocuments,
          totalSize: backupSize,
          duration,
          success: true
        }
      };

      await updateDoc(doc(db, 'backups', backupId), finalMetadata);

      // Mettre à jour les stats
      this.backupStats = {
        lastBackup: backupId,
        totalBackups: this.backupStats.totalBackups + 1,
        lastBackupSize: backupSize,
        lastBackupDuration: duration
      };

      console.log(`✅ Sauvegarde terminée: ${backupId}`);
      console.log(`📊 ${totalDocuments} documents, ${formatFileSize(backupSize)}, ${Math.round(duration/1000)}s`);

      this.notifyProgress(backupId, 'completed', 100, 'Sauvegarde terminée avec succès !');
      this.notifyStatus('backup_success', {
        backupId,
        documentsCount: totalDocuments,
        size: backupSize,
        duration
      });

      return {
        success: true,
        id: backupId,
        message: `Sauvegarde créée avec succès`,
        metadata: {
          totalDocuments,
          totalSize: backupSize,
          duration,
          collectionsCount: collectionsToBackup.length,
          downloadURL
        }
      };

    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde:', error);
      
      // Marquer comme échouée
      try {
        await updateDoc(doc(db, 'backups', backupId), {
          status: 'failed',
          error: error.message,
          failedAt: createTimestamp()
        });
      } catch (updateError) {
        console.error('❌ Erreur mise à jour statut:', updateError);
      }

      this.notifyProgress(backupId, 'failed', 0, `Erreur: ${error.message}`);
      this.notifyStatus('backup_failure', { backupId, error: error.message });

      throw error;

    } finally {
      this.isBackupInProgress = false;
      this.currentBackupId = null;
    }
  }

  /**
   * Sauvegarder une collection spécifique
   */
  async backupCollection(collectionName) {
    try {
      console.log(`📂 Sauvegarde collection: ${collectionName}`);
      
      const collectionRef = collection(db, collectionName);
      const snapshot = await getDocs(collectionRef);
      
      const documents = [];
      snapshot.forEach(doc => {
        documents.push({
          id: doc.id,
          data: doc.data()
        });
      });

      console.log(`✅ Collection ${collectionName}: ${documents.length} documents`);

      return {
        name: collectionName,
        count: documents.length,
        documents,
        timestamp: Date.now(),
        size: JSON.stringify(documents).length
      };

    } catch (error) {
      console.error(`❌ Erreur sauvegarde collection ${collectionName}:`, error);
      throw error;
    }
  }

  /**
   * ====================================
   * MÉTHODES DE RESTAURATION
   * ====================================
   */

  /**
   * Restaurer depuis une sauvegarde
   */
  async restoreFromBackup(backupId, options = {}) {
    const {
      userId = 'system',
      collectionsToRestore = null,
      mode = 'merge', // 'merge' ou 'replace'
      createBackupBefore = true
    } = options;

    console.log(`🔄 Démarrage restauration: ${backupId}`);
    console.log(`🎛️ Mode: ${mode}, Collections: ${collectionsToRestore?.join(', ') || 'toutes'}`);

    const startTime = Date.now();

    try {
      // Charger les données de sauvegarde
      const backupData = await this.loadBackupData(backupId);
      
      if (!backupData) {
        throw new Error('Données de sauvegarde introuvables');
      }

      // Vérifier l'intégrité
      const integrity = validateBackupIntegrity(backupData);
      if (!integrity.isValid) {
        throw new Error(`Sauvegarde corrompue: ${integrity.errors.join(', ')}`);
      }

      // Créer une sauvegarde de sécurité si demandé
      if (createBackupBefore) {
        console.log('🛡️ Création sauvegarde de sécurité...');
        await this.createFullBackup({
          userId,
          type: 'pre-restore',
          description: `Sauvegarde avant restauration de ${backupId}`
        });
      }

      // Déterminer les collections à restaurer
      const collections = collectionsToRestore || Object.keys(backupData.collections);
      console.log(`📂 Collections à restaurer: ${collections.join(', ')}`);

      let restoredDocuments = 0;
      const errors = [];

      // Restaurer chaque collection
      for (const collectionName of collections) {
        try {
          console.log(`🔄 Restauration collection: ${collectionName}`);
          
          const collectionData = backupData.collections[collectionName];
          if (!collectionData || collectionData.error) {
            errors.push(`Collection ${collectionName} non disponible dans la sauvegarde`);
            continue;
          }

          const restored = await this.restoreCollection(collectionName, collectionData, mode);
          restoredDocuments += restored;

        } catch (error) {
          console.error(`❌ Erreur restauration collection ${collectionName}:`, error);
          errors.push(`${collectionName}: ${error.message}`);
        }
      }

      // Enregistrer l'opération de restauration
      await addDoc(collection(db, 'restores'), {
        backupId,
        restoredBy: userId,
        restoredAt: createTimestamp(),
        mode,
        collectionsRestored: collections,
        documentsRestored: restoredDocuments,
        duration: Date.now() - startTime,
        errors: errors.length > 0 ? errors : null,
        success: true
      });

      console.log(`✅ Restauration terminée: ${restoredDocuments} documents`);

      this.notifyStatus('restore_success', {
        backupId,
        documentsRestored: restoredDocuments,
        duration: Date.now() - startTime
      });

      return {
        success: true,
        message: 'Restauration terminée avec succès',
        documentsRestored: restoredDocuments,
        collectionsRestored: collections.length,
        errors: errors.length > 0 ? errors : null,
        duration: Date.now() - startTime
      };

    } catch (error) {
      console.error('❌ Erreur lors de la restauration:', error);
      
      // Enregistrer l'échec
      try {
        await addDoc(collection(db, 'restores'), {
          backupId,
          restoredBy: userId,
          restoredAt: createTimestamp(),
          mode,
          error: error.message,
          success: false
        });
      } catch (logError) {
        console.error('❌ Erreur log restauration:', logError);
      }

      this.notifyStatus('restore_failure', { backupId, error: error.message });

      throw error;
    }
  }

  /**
   * Restaurer une collection spécifique
   */
  async restoreCollection(collectionName, collectionData, mode = 'merge') {
    console.log(`🔄 Restauration collection ${collectionName} (mode: ${mode})`);

    try {
      const collectionRef = collection(db, collectionName);
      let restoredCount = 0;

      // Mode remplacement : supprimer tous les documents existants
      if (mode === 'replace') {
        console.log(`🗑️ Suppression documents existants dans ${collectionName}`);
        const existingDocs = await getDocs(collectionRef);
        
        // Supprimer par batches
        const batches = [];
        let currentBatch = writeBatch(db);
        let batchCount = 0;

        existingDocs.forEach(doc => {
          currentBatch.delete(doc.ref);
          batchCount++;

          if (batchCount >= BACKUP_CONFIG.LIMITS.FIRESTORE_BATCH_SIZE) {
            batches.push(currentBatch);
            currentBatch = writeBatch(db);
            batchCount = 0;
          }
        });

        if (batchCount > 0) {
          batches.push(currentBatch);
        }

        // Exécuter les batches de suppression
        for (const batch of batches) {
          await batch.commit();
        }
      }

      // Restaurer les documents par batches
      const documents = collectionData.documents || [];
      const batches = [];
      let currentBatch = writeBatch(db);
      let batchCount = 0;

      for (const docData of documents) {
        const docRef = doc(collectionRef, docData.id);
        currentBatch.set(docRef, docData.data);
        batchCount++;
        restoredCount++;

        if (batchCount >= BACKUP_CONFIG.LIMITS.FIRESTORE_BATCH_SIZE) {
          batches.push(currentBatch);
          currentBatch = writeBatch(db);
          batchCount = 0;
        }
      }

      if (batchCount > 0) {
        batches.push(currentBatch);
      }

      // Exécuter les batches de restauration
      for (const batch of batches) {
        await batch.commit();
      }

      console.log(`✅ Collection ${collectionName}: ${restoredCount} documents restaurés`);
      return restoredCount;

    } catch (error) {
      console.error(`❌ Erreur restauration collection ${collectionName}:`, error);
      throw error;
    }
  }

  /**
   * ====================================
   * MÉTHODES DE GESTION DES SAUVEGARDES
   * ====================================
   */

  /**
   * Lister les sauvegardes
   */
  async listBackups(options = {}) {
    const {
      limit: queryLimit = 50,
      status = null,
      type = null,
      userId = null,
      startDate = null,
      endDate = null
    } = options;

    try {
      let backupsQuery = collection(db, 'backups');

      // Construire la requête avec les filtres
      const constraints = [orderBy('createdAt', 'desc')];

      if (status) {
        constraints.push(where('status', '==', status));
      }
      
      if (type) {
        constraints.push(where('type', '==', type));
      }
      
      if (userId) {
        constraints.push(where('createdBy', '==', userId));
      }

      if (queryLimit) {
        constraints.push(limit(queryLimit));
      }

      backupsQuery = query(backupsQuery, ...constraints);
      const snapshot = await getDocs(backupsQuery);
      
      const backups = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        backups.push({
          id: doc.id,
          ...data,
          // Convertir les Timestamps en dates
          createdAt: data.createdAt?.toDate?.() || data.createdAt,
          startedAt: data.startedAt?.toDate?.() || data.startedAt,
          completedAt: data.completedAt?.toDate?.() || data.completedAt,
          timestamp: data.createdAt?.toMillis?.() || data.timestamp || Date.now()
        });
      });

      // Filtrer par dates si nécessaire (post-requête car Firestore a des limitations)
      let filteredBackups = backups;
      
      if (startDate) {
        const start = new Date(startDate);
        filteredBackups = filteredBackups.filter(backup => 
          new Date(backup.timestamp) >= start
        );
      }
      
      if (endDate) {
        const end = new Date(endDate);
        filteredBackups = filteredBackups.filter(backup => 
          new Date(backup.timestamp) <= end
        );
      }

      console.log(`📋 ${filteredBackups.length} sauvegardes trouvées`);
      return filteredBackups;

    } catch (error) {
      console.error('❌ Erreur listage sauvegardes:', error);
      throw error;
    }
  }

  /**
   * Obtenir une sauvegarde spécifique
   */
  async getBackup(backupId) {
    try {
      const backupDoc = await getDoc(doc(db, 'backups', backupId));
      
      if (!backupDoc.exists()) {
        throw new Error('Sauvegarde introuvable');
      }

      const data = backupDoc.data();
      return {
        id: backupDoc.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || data.createdAt,
        startedAt: data.startedAt?.toDate?.() || data.startedAt,
        completedAt: data.completedAt?.toDate?.() || data.completedAt,
        timestamp: data.createdAt?.toMillis?.() || data.timestamp || Date.now()
      };

    } catch (error) {
      console.error(`❌ Erreur récupération backup ${backupId}:`, error);
      throw error;
    }
  }

  /**
   * Charger les données complètes d'une sauvegarde
   */
  async loadBackupData(backupId) {
    try {
      const backup = await this.getBackup(backupId);
      
      if (!backup.downloadURL && !backup.storageRef) {
        throw new Error('URL de téléchargement manquante');
      }

      // Charger depuis Firebase Storage
      let backupData;
      
      if (backup.downloadURL) {
        // Télécharger via URL
        const response = await fetch(backup.downloadURL);
        if (!response.ok) {
          throw new Error(`Erreur téléchargement: ${response.status}`);
        }
        backupData = await response.json();
      } else if (backup.storageRef) {
        // Télécharger via référence Storage
        const storageRef = ref(storage, backup.storageRef);
        const downloadURL = await getDownloadURL(storageRef);
        const response = await fetch(downloadURL);
        if (!response.ok) {
          throw new Error(`Erreur téléchargement: ${response.status}`);
        }
        backupData = await response.json();
      }

      console.log(`📥 Données sauvegarde ${backupId} chargées`);
      return backupData;

    } catch (error) {
      console.error(`❌ Erreur chargement données ${backupId}:`, error);
      throw error;
    }
  }

  /**
   * Supprimer une sauvegarde
   */
  async deleteBackup(backupId) {
    try {
      console.log(`🗑️ Suppression sauvegarde: ${backupId}`);

      // Récupérer les infos de la sauvegarde
      const backup = await this.getBackup(backupId);

      // Supprimer le fichier de Storage
      if (backup.storageRef) {
        try {
          const storageRef = ref(storage, backup.storageRef);
          await deleteObject(storageRef);
          console.log(`🗑️ Fichier Storage supprimé: ${backup.storageRef}`);
        } catch (storageError) {
          console.warn(`⚠️ Erreur suppression Storage:`, storageError);
          // Continuer même si la suppression Storage échoue
        }
      }

      // Supprimer l'entrée Firestore
      await deleteDoc(doc(db, 'backups', backupId));

      console.log(`✅ Sauvegarde ${backupId} supprimée`);
      return { success: true, message: 'Sauvegarde supprimée avec succès' };

    } catch (error) {
      console.error(`❌ Erreur suppression backup ${backupId}:`, error);
      throw error;
    }
  }

  /**
   * ====================================
   * MÉTHODES UTILITAIRES
   * ====================================
   */

  /**
   * Déterminer les collections à sauvegarder
   */
  getCollectionsToBackup(options = {}) {
    const {
      includeCritical = true,
      includeSystem = true,
      includeOptional = false,
      customCollections = null
    } = options;

    if (customCollections) {
      return customCollections;
    }

    let collections = [];

    if (includeCritical) {
      collections = collections.concat(BACKUP_CONFIG.CRITICAL_COLLECTIONS);
    }

    if (includeSystem) {
      collections = collections.concat(BACKUP_CONFIG.SYSTEM_COLLECTIONS);
    }

    if (includeOptional) {
      collections = collections.concat(BACKUP_CONFIG.OPTIONAL_COLLECTIONS);
    }

    // Supprimer les doublons
    return [...new Set(collections)];
  }

  /**
   * Obtenir les statistiques générales
   */
  async getBackupStatistics() {
    try {
      const backups = await this.listBackups({ limit: 100 });
      
      const stats = {
        total: backups.length,
        completed: backups.filter(b => b.status === 'completed').length,
        failed: backups.filter(b => b.status === 'failed').length,
        inProgress: backups.filter(b => b.status === 'in_progress').length,
        totalSize: 0,
        averageSize: 0,
        averageDuration: 0,
        successRate: 0,
        lastBackup: null,
        oldestBackup: null
      };

      if (backups.length > 0) {
        const completedBackups = backups.filter(b => b.status === 'completed');
        
        if (completedBackups.length > 0) {
          stats.totalSize = completedBackups.reduce((sum, b) => sum + (b.metadata?.totalSize || 0), 0);
          stats.averageSize = stats.totalSize / completedBackups.length;
          stats.averageDuration = completedBackups.reduce((sum, b) => sum + (b.metadata?.duration || 0), 0) / completedBackups.length;
        }

        stats.successRate = (stats.completed / stats.total) * 100;
        stats.lastBackup = backups[0];
        stats.oldestBackup = backups[backups.length - 1];
      }

      return stats;

    } catch (error) {
      console.error('❌ Erreur statistiques:', error);
      throw error;
    }
  }

  /**
   * Vérifier l'état du service
   */
  getServiceStatus() {
    return {
      isBackupInProgress: this.isBackupInProgress,
      currentBackupId: this.currentBackupId,
      stats: this.backupStats,
      timestamp: Date.now()
    };
  }

  /**
   * ====================================
   * SYSTÈME DE NOTIFICATIONS
   * ====================================
   */

  /**
   * S'abonner aux notifications de progression
   */
  onProgress(callback) {
    this.progressCallbacks.add(callback);
    return () => this.progressCallbacks.delete(callback);
  }

  /**
   * S'abonner aux notifications de statut
   */
  onStatus(callback) {
    this.statusCallbacks.add(callback);
    return () => this.statusCallbacks.delete(callback);
  }

  /**
   * Notifier la progression
   */
  notifyProgress(backupId, status, progress, message) {
    const notification = {
      backupId,
      status,
      progress,
      message,
      timestamp: Date.now()
    };

    this.progressCallbacks.forEach(callback => {
      try {
        callback(notification);
      } catch (error) {
        console.error('❌ Erreur callback progression:', error);
      }
    });
  }

  /**
   * Notifier le statut
   */
  notifyStatus(eventType, data = {}) {
    const notification = {
      type: eventType,
      data,
      timestamp: Date.now()
    };

    this.statusCallbacks.forEach(callback => {
      try {
        callback(notification);
      } catch (error) {
        console.error('❌ Erreur callback statut:', error);
      }
    });
  }

  /**
   * ====================================
   * MÉTHODES DE MAINTENANCE
   * ====================================
   */

  /**
   * Nettoyer les anciennes sauvegardes
   */
  async cleanupOldBackups(retentionPolicy = null) {
    const policy = retentionPolicy || BACKUP_CONFIG.RETENTION;
    
    try {
      console.log('🧹 Nettoyage des anciennes sauvegardes...');
      
      const allBackups = await this.listBackups({ limit: 1000 });
      const now = Date.now();
      let deletedCount = 0;

      // Grouper par type de rétention
      const backupsByAge = {
        hourly: allBackups.filter(b => {
          const age = now - b.timestamp;
          return age <= 2 * 24 * 60 * 60 * 1000; // 2 jours
        }),
        daily: allBackups.filter(b => {
          const age = now - b.timestamp;
          return age > 2 * 24 * 60 * 60 * 1000 && age <= 30 * 24 * 60 * 60 * 1000; // 2-30 jours
        }),
        weekly: allBackups.filter(b => {
          const age = now - b.timestamp;
          return age > 30 * 24 * 60 * 60 * 1000 && age <= 90 * 24 * 60 * 60 * 1000; // 30-90 jours
        }),
        monthly: allBackups.filter(b => {
          const age = now - b.timestamp;
          return age > 90 * 24 * 60 * 60 * 1000 && age <= 365 * 24 * 60 * 60 * 1000; // 90-365 jours
        }),
        yearly: allBackups.filter(b => {
          const age = now - b.timestamp;
          return age > 365 * 24 * 60 * 60 * 1000; // Plus d'1 an
        })
      };

      // Appliquer la politique de rétention
      for (const [ageGroup, backups] of Object.entries(backupsByAge)) {
        const maxKeep = policy[ageGroup.toUpperCase()]?.keep || 0;
        
        if (backups.length > maxKeep) {
          // Trier par date (plus anciens en premier)
          backups.sort((a, b) => a.timestamp - b.timestamp);
          
          // Supprimer les plus anciens
          const toDelete = backups.slice(0, backups.length - maxKeep);
          
          for (const backup of toDelete) {
            try {
              await this.deleteBackup(backup.id);
              deletedCount++;
              console.log(`🗑️ Supprimé: ${backup.id} (${ageGroup})`);
            } catch (error) {
              console.error(`❌ Erreur suppression ${backup.id}:`, error);
            }
          }
        }
      }

      console.log(`✅ Nettoyage terminé: ${deletedCount} sauvegardes supprimées`);
      return { deletedCount };

    } catch (error) {
      console.error('❌ Erreur nettoyage:', error);
      throw error;
    }
  }

  /**
   * Réparer une sauvegarde corrompue
   */
  async repairBackup(backupId) {
    try {
      console.log(`🔧 Réparation sauvegarde: ${backupId}`);
      
      const backup = await this.getBackup(backupId);
      
      if (backup.status === 'completed') {
        console.log('✅ Sauvegarde déjà complète');
        return { success: true, message: 'Sauvegarde déjà en bon état' };
      }

      // Tenter de charger les données
      try {
        const backupData = await this.loadBackupData(backupId);
        const integrity = validateBackupIntegrity(backupData);
        
        if (integrity.isValid) {
          // Marquer comme réparée
          await updateDoc(doc(db, 'backups', backupId), {
            status: 'completed',
            repairedAt: createTimestamp(),
            repairNotes: 'Réparée automatiquement - données intègres'
          });
          
          console.log(`✅ Sauvegarde ${backupId} réparée`);
          return { success: true, message: 'Sauvegarde réparée avec succès' };
        }
      } catch (dataError) {
        console.error('❌ Données irrécupérables:', dataError);
      }

      // Si impossible à réparer, marquer comme corrompue
      await updateDoc(doc(db, 'backups', backupId), {
        status: 'corrupted',
        corruptedAt: createTimestamp(),
        corruptionReason: 'Données irrécupérables lors de la réparation'
      });

      return { 
        success: false, 
        message: 'Sauvegarde corrompue - données irrécupérables' 
      };

    } catch (error) {
      console.error(`❌ Erreur réparation ${backupId}:`, error);
      throw error;
    }
  }

  /**
   * Vérifier l'intégrité de toutes les sauvegardes
   */
  async verifyAllBackups() {
    try {
      console.log('🔍 Vérification intégrité de toutes les sauvegardes...');
      
      const backups = await this.listBackups({ limit: 100 });
      const results = {
        total: backups.length,
        verified: 0,
        corrupted: 0,
        errors: 0,
        details: []
      };

      for (const backup of backups) {
        try {
          if (backup.status !== 'completed') {
            results.details.push({
              id: backup.id,
              status: 'skipped',
              reason: `Statut: ${backup.status}`
            });
            continue;
          }

          const backupData = await this.loadBackupData(backup.id);
          const integrity = validateBackupIntegrity(backupData);
          
          if (integrity.isValid) {
            results.verified++;
            results.details.push({
              id: backup.id,
              status: 'ok',
              score: integrity.score
            });
          } else {
            results.corrupted++;
            results.details.push({
              id: backup.id,
              status: 'corrupted',
              errors: integrity.errors,
              warnings: integrity.warnings
            });
          }

        } catch (error) {
          results.errors++;
          results.details.push({
            id: backup.id,
            status: 'error',
            error: error.message
          });
        }
      }

      console.log(`✅ Vérification terminée: ${results.verified}/${results.total} OK`);
      return results;

    } catch (error) {
      console.error('❌ Erreur vérification globale:', error);
      throw error;
    }
  }

  /**
   * ====================================
   * MÉTHODES DE MONITORING AVANCÉ
   * ====================================
   */

  /**
   * Surveiller l'espace de stockage
   */
  async getStorageUsage() {
    try {
      const backupsRef = ref(storage, 'backups/');
      const backupsList = await listAll(backupsRef);
      
      let totalSize = 0;
      const files = [];

      for (const fileRef of backupsList.items) {
        try {
          const metadata = await getMetadata(fileRef);
          const size = metadata.size || 0;
          totalSize += size;
          
          files.push({
            name: fileRef.name,
            size,
            created: metadata.timeCreated,
            updated: metadata.updated
          });
        } catch (metaError) {
          console.warn(`⚠️ Impossible de lire les métadonnées: ${fileRef.name}`);
        }
      }

      return {
        totalSize,
        fileCount: files.length,
        files: files.sort((a, b) => new Date(b.created) - new Date(a.created)),
        averageFileSize: files.length > 0 ? totalSize / files.length : 0
      };

    } catch (error) {
      console.error('❌ Erreur usage stockage:', error);
      throw error;
    }
  }

/**
 * Surveiller l'espace de stockage
 */
async getStorageUsage() {
  try {
    const backupsRef = ref(storage, 'backups/');
    const backupsList = await listAll(backupsRef);
    
    let totalSize = 0;
    const files = [];

    for (const fileRef of backupsList.items) {
      try {
        const metadata = await getMetadata(fileRef);
        const size = metadata.size || 0;
        totalSize += size;
        
        files.push({
          name: fileRef.name,
          size,
          created: metadata.timeCreated,
          updated: metadata.updated
        });
      } catch (metaError) {
        console.warn(`⚠️ Impossible de lire les métadonnées: ${fileRef.name}`, metaError);
        // Continuer même si on ne peut pas lire un fichier
      }
    }

    console.log(`📊 Stockage total: ${totalSize} bytes, ${files.length} fichiers`);

    return {
      totalSize,
      fileCount: files.length,
      files: files.sort((a, b) => new Date(b.created) - new Date(a.created)),
      averageFileSize: files.length > 0 ? totalSize / files.length : 0
    };

  } catch (error) {
    console.error('❌ Erreur usage stockage:', error);
    
    // Fallback : calculer depuis les métadonnées des backups
    try {
      const backups = await this.listBackups({ limit: 100 });
      const totalSize = backups.reduce((sum, backup) => sum + (backup.metadata?.totalSize || 0), 0);
      
      console.log(`📊 Stockage calculé depuis les backups: ${totalSize} bytes`);
      
      return {
        totalSize,
        fileCount: backups.length,
        files: [],
        averageFileSize: backups.length > 0 ? totalSize / backups.length : 0
      };
    } catch (fallbackError) {
      console.error('❌ Erreur fallback stockage:', fallbackError);
      throw new Error(`Impossible d'accéder au stockage: ${error.message}`);
    }
  }
}

  /**
   * Obtenir l'historique des restaurations
   */
  async getRestoreHistory(options = {}) {
    const { limit: queryLimit = 20 } = options;

    try {
      const restoresQuery = query(
        collection(db, 'restores'),
        orderBy('restoredAt', 'desc'),
        limit(queryLimit)
      );

      const snapshot = await getDocs(restoresQuery);
      const restores = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        restores.push({
          id: doc.id,
          ...data,
          restoredAt: data.restoredAt?.toDate?.() || data.restoredAt
        });
      });

      return restores;

    } catch (error) {
      console.error('❌ Erreur historique restaurations:', error);
      throw error;
    }
  }

  /**
   * ====================================
   * MÉTHODES D'EXPORT/IMPORT
   * ====================================
   */

  /**
   * Exporter les métadonnées de toutes les sauvegardes
   */
  async exportBackupMetadata() {
    try {
      const backups = await this.listBackups({ limit: 1000 });
      const exportData = {
        exportedAt: new Date().toISOString(),
        totalBackups: backups.length,
        backups: backups.map(backup => ({
          id: backup.id,
          name: backup.name,
          type: backup.type,
          status: backup.status,
          createdAt: backup.createdAt,
          createdBy: backup.createdBy,
          metadata: backup.metadata
        }))
      };

      return exportData;

    } catch (error) {
      console.error('❌ Erreur export métadonnées:', error);
      throw error;
    }
  }

  /**
   * Créer un rapport de sauvegarde
   */
  async generateBackupReport(options = {}) {
    const { 
      includePeriod = 30, // derniers 30 jours
      includePerformance = true,
      includeStorage = true,
      includeIntegrity = false 
    } = options;

    try {
      console.log('📊 Génération rapport de sauvegarde...');

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - includePeriod);

      // Données de base
      const backups = await this.listBackups({ 
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        limit: 1000 
      });

      const report = {
        generatedAt: new Date().toISOString(),
        period: {
          start: startDate.toISOString(),
          end: endDate.toISOString(),
          days: includePeriod
        },
        summary: {
          totalBackups: backups.length,
          successful: backups.filter(b => b.status === 'completed').length,
          failed: backups.filter(b => b.status === 'failed').length,
          inProgress: backups.filter(b => b.status === 'in_progress').length,
          successRate: 0
        },
        backups: backups
      };

      report.summary.successRate = report.summary.totalBackups > 0 
        ? (report.summary.successful / report.summary.totalBackups) * 100 
        : 0;

      // Ajouter les données de performance
      if (includePerformance) {
        report.performance = await this.analyzePerformance();
      }

      // Ajouter les données de stockage
      if (includeStorage) {
        report.storage = await this.getStorageUsage();
      }

      // Ajouter la vérification d'intégrité
      if (includeIntegrity) {
        report.integrity = await this.verifyAllBackups();
      }

      console.log('✅ Rapport généré avec succès');
      return report;

    } catch (error) {
      console.error('❌ Erreur génération rapport:', error);
      throw error;
    }
  }

  /**
   * ====================================
   * MÉTHODES DE TEST ET DEBUG
   * ====================================
   */

  /**
   * Créer une sauvegarde de test
   */
  async createTestBackup() {
    console.log('🧪 Création sauvegarde de test...');
    
    try {
      const result = await this.createFullBackup({
        userId: 'test-user',
        type: 'test',
        description: 'Sauvegarde de test générée automatiquement',
        includeCritical: true,
        includeSystem: false,
        includeOptional: false
      });

      console.log('✅ Sauvegarde de test créée:', result.id);
      return result;

    } catch (error) {
      console.error('❌ Erreur sauvegarde de test:', error);
      throw error;
    }
  }

  /**
   * Valider la configuration du service
   */
  validateConfiguration() {
    const issues = [];

    // Vérifier la configuration Firebase
    if (!db) {
      issues.push('Base de données Firestore non initialisée');
    }

    if (!storage) {
      issues.push('Firebase Storage non initialisé');
    }

    // Vérifier la configuration de sauvegarde
    if (!BACKUP_CONFIG.CRITICAL_COLLECTIONS || BACKUP_CONFIG.CRITICAL_COLLECTIONS.length === 0) {
      issues.push('Aucune collection critique configurée');
    }

    // Vérifier les permissions
    if (!BACKUP_CONFIG.SECURITY?.PERMISSIONS) {
      issues.push('Permissions de sécurité non configurées');
    }

    return {
      isValid: issues.length === 0,
      issues,
      configuration: {
        criticalCollections: BACKUP_CONFIG.CRITICAL_COLLECTIONS?.length || 0,
        systemCollections: BACKUP_CONFIG.SYSTEM_COLLECTIONS?.length || 0,
        optionalCollections: BACKUP_CONFIG.OPTIONAL_COLLECTIONS?.length || 0,
        retentionPolicyConfigured: !!BACKUP_CONFIG.RETENTION,
        limitsConfigured: !!BACKUP_CONFIG.LIMITS
      }
    };
  }

  /**
   * Obtenir les informations de debug
   */
  getDebugInfo() {
    return {
      service: {
        isBackupInProgress: this.isBackupInProgress,
        currentBackupId: this.currentBackupId,
        stats: this.backupStats
      },
      callbacks: {
        progressCallbacks: this.progressCallbacks.size,
        statusCallbacks: this.statusCallbacks.size
      },
      configuration: this.validateConfiguration(),
      environment: {
        hasFirestore: !!db,
        hasStorage: !!storage,
        backupConfigVersion: BACKUP_CONFIG.VERSION
      },
      timestamp: new Date().toISOString()
    };
  }
}

// Instance singleton
const backupService = new BackupService();
export default backupService;